import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Conversation } from '@/types/Conversation';
import { Contact } from '@/types/Contact';
import ContactDisplay from '@/components/common/ContactDisplay';
import { Phone, ChevronRight, Clock, ChevronLeft } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import ConversationBillingBadge from '@/components/page-components/project/agents/conversations/ConversationBillingBadge';
import ConversationFlags from '@/components/page-components/project/agents/conversations/ConversationFlags';
import { getConversationBillingState } from '@/utils/conversationBilling';
import type { Agent } from '@/types/Agents';
import { useDateFormat } from '@/hooks/useDateFormat';

interface ConversationTableProps {
  conversations: Conversation[];
  onViewConversation?: (conversation: Conversation) => void;
  requestSort: (key: string, subKey?: string) => void;
  contacts?: Contact[];
  agentId?: number;
  subscription?: Agent['subscription'];
  /** Held in the URL by the agent page, so Back and a reload both restore it. */
  page: number;
  onPageChange: (page: number) => void;
}

const CONVERSATIONS_PER_PAGE = 20;

export const ConversationTable: React.FC<ConversationTableProps> = ({
  conversations,
  requestSort,
  contacts = [],
  agentId,
  subscription,
  page,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const df = useDateFormat();
  const navigate = useNavigate();
  const { agent_id } = useParams<{ agent_id: string }>();
  const isMobile = useIsMobile();
  const location = useLocation();

  // Calculate pagination
  const totalPages = Math.max(
    1,
    Math.ceil(conversations.length / CONVERSATIONS_PER_PAGE)
  );
  // Filtering can leave the URL pointing past the end of a shorter list.
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const paginatedConversations = useMemo(() => {
    const startIndex = (currentPage - 1) * CONVERSATIONS_PER_PAGE;
    const endIndex = startIndex + CONVERSATIONS_PER_PAGE;
    return conversations.slice(startIndex, endIndex);
  }, [conversations, currentPage]);

  // Clamp rather than reset: snapping to page 1 on every change in length is
  // what made Back lose your place, and the URL is the source of truth now.
  React.useEffect(() => {
    if (page > totalPages) onPageChange(totalPages);
  }, [page, totalPages, onPageChange]);

  const handleConversationClick = (conversation: Conversation) => {
    // Carry the list's query along so Back can restore the page and filters
    // without putting them on the conversation's own URL. A reload there loses
    // the state and falls back to an unfiltered list, which is the right
    // degradation for something this cosmetic.
    navigate(`/agents/${agent_id}/conversation/${conversation.id}`, {
      state: { listSearch: location.search },
    });
  };

  const formatDuration = (duration: number | undefined) => {
    if (!duration) {
      return t('agent.conversations.table.duration.zero.translation');
    }
    if (duration < 60) {
      return `${duration}s`;
    }
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}m ${seconds}s`;
  };

  const handlePreviousPage = () => {
    onPageChange(Math.max(1, currentPage - 1));
  };

  const handleNextPage = () => {
    onPageChange(Math.min(totalPages, currentPage + 1));
  };

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 text-4xl text-muted-foreground">
          {t(
            'agent.conversations.no_conversations.translation',
            'No conversations found'
          )}
        </div>
        <div className="text-xl text-muted-foreground">
          {t(
            'agent.conversations.no_conversations_description.translation',
            'Conversations will appear here once the agent starts receiving calls'
          )}
        </div>
      </div>
    );
  }

  // Mobile card layout
  if (isMobile) {
    return (
      <div className="flex flex-col gap-4">
        <Card className="p-2">
          <div className="space-y-1">
            {paginatedConversations.map((conversation, index) => (
              <div
                key={conversation.id}
                className={`flex cursor-pointer flex-col rounded-xl p-3 transition-colors hover:bg-muted/40 ${
                  index < paginatedConversations.length - 1
                    ? 'border-b border-border'
                    : ''
                }`}
                onClick={() => handleConversationClick(conversation)}
              >
                <div className="flex items-center justify-between min-w-0">
                  <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <span className="truncate">
                      <ContactDisplay
                        callerId={conversation.caller_id}
                        agentId={agentId}
                        contacts={contacts}
                        variant="compact"
                      />
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <ConversationFlags conversation={conversation} compact />
                    <ConversationBillingBadge
                      state={getConversationBillingState(
                        conversation,
                        subscription
                      )}
                    />
                    {conversation.duration && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span className="font-semibold text-foreground">
                          {formatDuration(conversation.duration)}
                        </span>
                      </div>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                <div className="mt-2 line-clamp-1 text-sm text-muted-foreground">
                  {conversation.summary || (
                    <span className="italic text-muted-foreground">
                      {t(
                        'agent.conversations.summary.no_summary.translation',
                        'No summary available'
                      )}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('common.previous.translation')}
            </Button>
            <span className="text-sm text-muted-foreground">
              {t('common.pagination.page_of.translation', {
                current: currentPage,
                total: totalPages,
              })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1"
            >
              {t('common.next.translation')}
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  const conversationTableColGroup = (
    <colgroup>
      <col className="w-[22%]" />
      <col />
      <col className="w-[12%]" />
      <col className="w-[18%]" />
      <col className="w-[15%]" />
    </colgroup>
  );

  const conversationTableHeader = (
    <TableHeader className="[&_tr]:border-b-0 [&_tr:hover]:bg-transparent dark:[&_tr:hover]:bg-transparent">
      <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
        <TableHead className="py-1">
          <Button
            variant="link"
            className="text-sm"
            onClick={() => requestSort('caller_id')}
          >
            {t('agent.conversations.table.headers.caller.translation')}
          </Button>
        </TableHead>
        <TableHead className="py-1">
          <span className="text-sm">
            {t('agent.conversations.table.headers.preview.translation')}
          </span>
        </TableHead>
        <TableHead className="py-1 text-center">
          <Button
            className="text-sm"
            variant="link"
            onClick={() => requestSort('duration')}
          >
            {t('agent.conversations.table.headers.duration.translation')}
          </Button>
        </TableHead>
        <TableHead className="text-right py-1 hidden xl:table-cell">
          <Button
            variant="link"
            className="text-sm"
            onClick={() => requestSort('created_at')}
          >
            {t('agent.conversations.table.headers.created_at.translation')}
          </Button>
        </TableHead>
        <TableHead className="py-1 text-right">
          <span className="text-sm">
            {t(
              'agent.conversations.table.headers.flags.translation',
              'Outcome'
            )}
          </span>
        </TableHead>
      </TableRow>
    </TableHeader>
  );

  // Desktop table layout
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="px-6">
          <Table className="table-fixed">
            {conversationTableColGroup}
            {conversationTableHeader}
          </Table>
        </div>
        <Card className="px-4 py-2">
          <Table className="table-fixed border-separate [border-spacing:0_0.125rem]">
            {conversationTableColGroup}
            <TableBody>
              {paginatedConversations.map(conversation => (
                <TableRow
                  key={conversation.id}
                  className="group cursor-pointer border-b-0 hover:bg-transparent dark:hover:bg-transparent"
                  onClick={() => handleConversationClick(conversation)}
                >
                  <TableCell className="rounded-l-full px-4 py-3 text-sm text-foreground transition-colors group-hover:bg-muted/40">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="min-w-0 truncate">
                        <ContactDisplay
                          callerId={conversation.caller_id}
                          agentId={agentId}
                          contacts={contacts}
                          variant="default"
                          className="text-muted-foreground"
                        />
                      </span>
                      <ConversationBillingBadge
                        state={getConversationBillingState(
                          conversation,
                          subscription
                        )}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs truncate px-4 py-3 text-sm text-foreground transition-colors group-hover:bg-muted/40">
                    {conversation.summary ? (
                      conversation.summary
                    ) : (
                      <span className="italic text-muted-foreground">
                        {t(
                          'agent.conversations.summary.no_summary.translation',
                          'No summary available'
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-center text-sm text-foreground transition-colors group-hover:bg-muted/40">
                    {formatDuration(conversation.duration)}
                  </TableCell>
                  <TableCell className="hidden px-4 py-3 text-right text-sm text-foreground transition-colors group-hover:bg-muted/40 xl:table-cell">
                    {df.dateTime(conversation.created_at)}
                  </TableCell>
                  <TableCell className="rounded-r-full px-4 py-3 text-sm text-foreground transition-colors group-hover:bg-muted/40">
                    <ConversationFlags
                      conversation={conversation}
                      className="justify-end"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('common.previous.translation')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('common.pagination.page_of.translation', {
              current: currentPage,
              total: totalPages,
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1"
          >
            {t('common.next.translation')}
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
